/**
 * Message subresources, fetched by the page and handed to the document as
 * `blob:` URLs.
 *
 * ADR 0041 / plan/14 §2.4. Session binding (ADR 0040) means every request
 * inside `RequireSession` must present `X-Session-Proof`, and the three places
 * a browser issues an authenticated GET *for* us cannot carry one:
 *
 *   - an `<img src>` inside the sandboxed message frame (inline `cid:` parts,
 *     and the sealed remote refs of ADR 0018),
 *   - an `<a download>` on an attachment chip,
 *   - anything else the frame's own loader would reach for.
 *
 * So the page issues them instead, through the shared transport — the one
 * place the proof header is attached — and injects the result as an object
 * URL. Two things follow, and both are improvements:
 *
 *   1. The frame CSP tightens from `img-src 'self'` to `img-src blob:`. A
 *      `blob:` URL cannot reach the network, so a sanitizer bypass inside the
 *      frame has no ambient authority left to borrow.
 *   2. The decoded bytes now live in page memory for as long as the object URL
 *      does. That is the cost, and it is why every URL minted here is minted
 *      through an `ObjectUrlRegistry` that the owning component revokes on
 *      message change and on unmount. A reading pane someone pages through is
 *      exactly where a leak accumulates.
 */

import { API_BASE, ApiError, api } from '@/api';
import { ApiErrorCodes } from '@/api/errors';

/** Marks an image whose fetch failed, as distinct from one never requested. */
export const FAILED_SUBRESOURCE_CLASS = 'ols-subresource-failed';

/** Marks an image the server refused on policy, as distinct from one that failed. */
export const REFUSED_SUBRESOURCE_CLASS = 'ols-subresource-refused';

/**
 * Marks an SVG the server refused because the reader has not allowed SVG.
 * Separate from `REFUSED_SUBRESOURCE_CLASS` because it is the one refusal the
 * reader can change, and the pane offers exactly that (SvgNotice).
 */
export const SVG_BLOCKED_SUBRESOURCE_CLASS = 'ols-subresource-svg';

/** The proxy's `details.reason` for "an SVG, and SVG is not allowed here". */
export const SVG_NOT_ALLOWED_REASON = 'svg';

/*
 * ## SVG, and why this page never mints an SVG object URL
 *
 * ADR 0018's SVG amendment (backend `httpapi/remote.go`). The reader can allow
 * SVG images; the server sanitizes every one it serves. That is not enough on
 * its own, and this is the layer that makes the page safe regardless of the
 * sanitizer.
 *
 * Observed in real Chrome 153, against a stand-in for this page with the
 * app's actual document CSP (it sends none): an SVG carrying an inline
 * `<script>` and an `onload`, fetched by the page and handed to the frame as
 * `blob:` —
 *
 *   - inside the frame: inert. Rendered through `<img>`, SVG is in the
 *     browser's secure mode, and the frame has no script anyway.
 *   - the same `blob:` URL opened as a top-level page ("Open image in new
 *     tab" on the frame's context menu, or any navigation to it): the script
 *     and the handler RAN, with this app's origin, and read localStorage. A
 *     `blob:` URL belongs to the origin that minted it, and none of the
 *     proxy's protective response headers (`sandbox` CSP, nosniff) travel
 *     with the Blob. In this app that origin holds the session and the
 *     non-extractable device key in IndexedDB.
 *
 * So an SVG is rasterized to PNG before anything is minted (`rasterizeSvg`),
 * and `ObjectUrlRegistry.create` additionally refuses to mint any markup type
 * at all. A PNG opened in a tab is an image document; it runs nothing. The
 * cost is that an allowed SVG is shown as a bitmap — drawn at twice its size,
 * so it stays sharp on a high-density screen — rather than as vectors.
 *
 * Considered and not chosen: a `data:` URL in the frame (it would work — a
 * `data:` document has an opaque origin — but it needs `img-src data:` in the
 * frame CSP and leans on every browser's top-level `data:` policy), and
 * serving SVG as a non-SVG type (the `<img>` would then not render it at all).
 */

/** Raster types an `<img>` renders and a top-level tab shows as an inert image. */
const INERT_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
]);

/** A Blob's media type without parameters, lower-cased. */
export function baseType(type: string): string {
  return (type.split(';')[0] ?? '').trim().toLowerCase();
}

/**
 * True for the media types `ObjectUrlRegistry.create` lets keep their own MIME.
 *
 * Exported so the attachment previewer can ask the one authority rather than
 * keeping a second list: a type that is not on this list is minted as
 * `application/octet-stream`, which an `<img>` cannot decode and a tab can
 * only download — which is the whole point, and also the reason nothing else
 * may be previewed as an image.
 */
export function isInertImageType(type: string): boolean {
  return INERT_IMAGE_TYPES.has(baseType(type));
}

function isSvgBlob(blob: Blob): boolean {
  return baseType(blob.type) === 'image/svg+xml';
}

/** A rasterized SVG, and the CSS size the SVG itself would have had. */
export interface RasterImage {
  blob: Blob;
  width: number;
  height: number;
}

/** Turns an SVG Blob into a PNG. Injectable because jsdom has no canvas. */
export type SvgRasterizer = (svg: Blob, signal?: AbortSignal) => Promise<RasterImage>;

/** Device pixels per CSS pixel the PNG is drawn at: sharp on a 2x screen. */
const RASTER_SCALE = 2;
/** Longest edge, in CSS pixels, an SVG is drawn at before it is scaled down. */
const MAX_RASTER_EDGE = 2048;
/** The CSS default object size, for an SVG with no intrinsic size at all. */
const DEFAULT_OBJECT_SIZE = { width: 300, height: 150 } as const;

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('could not read the SVG'));
    reader.readAsDataURL(blob);
  });
}

/**
 * The real rasterizer: decode the SVG as an image, draw it to a canvas, keep
 * the PNG.
 *
 * The SVG is decoded from a `data:` URL, not a `blob:` one, so that not even
 * a transient same-origin SVG URL exists: a `data:` document has an opaque
 * origin. It is decoded through `Image`, i.e. in the same secure image mode
 * the frame would have used — no script, no external loads — and the element
 * is never attached to the document.
 */
export const rasterizeSvg: SvgRasterizer = async (svg, signal) => {
  const image = new Image();
  image.decoding = 'async';
  image.src = await readAsDataUrl(new Blob([svg], { type: 'image/svg+xml' }));
  await image.decode();
  if (signal?.aborted) throw signal.reason;

  let width = image.naturalWidth;
  let height = image.naturalHeight;
  if (!(width > 0 && height > 0)) {
    ({ width, height } = DEFAULT_OBJECT_SIZE);
  }
  const shrink = Math.min(1, MAX_RASTER_EDGE / Math.max(width, height));
  width = Math.max(1, Math.round(width * shrink));
  height = Math.max(1, Math.round(height * shrink));

  const canvas = document.createElement('canvas');
  canvas.width = width * RASTER_SCALE;
  canvas.height = height * RASTER_SCALE;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('no 2d canvas to rasterize the SVG');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (png === null) throw new Error('the SVG could not be rasterized');
  return { blob: png, width, height };
};

export interface ResolveOptions {
  /** Replaces the canvas rasterizer; tests only. */
  rasterizeSvg?: SvgRasterizer;
  /**
   * The message's stylesheet (`MessageDetail.bodyCss`, ADR 0081). Its
   * background images are resolved like the body's: fetched here, handed to
   * the frame as `blob:` URLs.
   */
  css?: string;
}

/**
 * How many subresource fetches the page keeps in flight at once.
 *
 * The remote-content proxy caps concurrent outbound fetches at 20 *per account*
 * (`remoteMaxConcurrentPerAccount`, backend/internal/httpapi/remote.go) and
 * answers the overflow with `429`. That cap is deliberate and must stay: a
 * message with hundreds of `<img>` must not become a matching outbound
 * fan-out at the sender's choosing. So the page's job is to stay under it,
 * not to discover it — and until this existed the page did neither. A thirty
 * image newsletter issued thirty parallel requests the instant the reader
 * consented, twenty were served, ten came back `429`, and ten images rendered
 * as empty boxes. From the reader's side that is "I clicked load images and
 * they did not load".
 *
 * Six, not nineteen. The server's budget is per account, and one account can
 * have the reading pane and a detached message window fetching at the same
 * time; neither knows about the other, so each has to leave room. Six also
 * sits at the browser's own per-origin connection limit, which is where these
 * requests would queue anyway.
 */
export const SUBRESOURCE_CONCURRENCY = 6;

/**
 * Attempts per subresource, the first included. Only `429` is ever retried:
 * it is the one failure that says "not now" rather than "no" — every other
 * refusal (tracker, no consent, guard block, gone) is a decision that will not
 * change on a second ask, and retrying it would be a second signal to the
 * sender for nothing.
 */
const RATE_LIMITED_ATTEMPTS = 3;

/**
 * Ceiling on the wait the server asks for. `Retry-After` arrives over the
 * network, and an unbounded sleep here would leave the reading pane showing
 * half a message for as long as the header said to.
 */
const MAX_RETRY_DELAY_MS = 5_000;

/** Fallback when the response carried no usable `Retry-After`. */
const DEFAULT_RETRY_DELAY_MS = 500;

/** What the parent must fetch itself. `signal` aborts an in-flight load. */
export type SubresourceFetcher = (path: string, signal?: AbortSignal) => Promise<Blob>;

/**
 * True for a URL that addresses our own authenticated API surface.
 *
 * The server sanitizer emits exactly two kinds of `img src` we are meant to
 * resolve — the rewritten part URL and the sealed remote ref (ADR 0017 §1 as
 * amended by ADR 0018) — and both live under `API_BASE`. Everything else,
 * including the `/remote-blocked.png` placeholder literal and anything a
 * sanitizer bug let through, is deliberately *not* fetched: a protocol-relative
 * `//evil.example/api/v1/x` does not start with `/api/v1/`, and an absolute
 * third-party URL does not either.
 */
export function isAuthenticatedSubresource(src: string): boolean {
  return src.startsWith(`${API_BASE}/`);
}

/**
 * Turns a backend-minted absolute path into the transport-relative one.
 *
 * `HttpTransport.buildUrl` prepends `API_BASE`, and both `Attachment.downloadUrl`
 * and the sanitizer's `img src` already carry it. Rebuilding the path from ids
 * instead would be worse: the backend mints these strings and only the exact
 * string it minted addresses the part.
 */
export function transportPath(url: string): string {
  if (!isAuthenticatedSubresource(url)) {
    throw new Error(`Refusing to fetch ${url}: not a path on this API.`);
  }
  return url.slice(API_BASE.length);
}

/*
 * The mock's stand-in image moved to the mock adapter (api.remoteContent
 * .subresource). It lived here while this module owned its own fetch path;
 * now that there is one path through the contract, the mock answers it like
 * every other method and there is nothing to special-case.
 */

/**
 * The one fetcher the app uses.
 *
 * It goes through the shared `transport`, not through `fetch`: the proof
 * header, the CSRF cookie, the `credentials: 'same-origin'` and the error
 * envelope parsing all live in `HttpTransport`, and a second transport here
 * would silently bypass session binding — which is the entire point of the
 * change this module implements.
 */
export const defaultSubresourceFetcher: SubresourceFetcher = (path, signal) =>
  // api.remoteContent.subresource, not a private call into the transport: one
  // fetch path means the proof header cannot be forgotten on this one, and the
  // mock adapter answers it in standalone mode like every other method.
  api.remoteContent.subresource(path, signal);

/**
 * Every object URL one component has minted, so it can revoke all of them at
 * once. A leaked `blob:` URL pins the whole decoded attachment in memory until
 * the document goes away.
 */
export class ObjectUrlRegistry {
  private urls = new Set<string>();

  /**
   * Mints an object URL — never one that could open as a document.
   *
   * A `blob:` URL carries this app's origin, and whatever type the Blob has
   * decides what a top-level navigation to it becomes. A raster image type
   * becomes an inert image document; anything else (SVG, HTML, XML, no type
   * at all) is re-typed `application/octet-stream`, which a browser only
   * ever downloads. The frame is unaffected: SVG never reaches here un-
   * rasterized (see `rasterizeSvg`), and the downloads that also use this
   * registry name their file through `<a download>`, not through the type.
   */
  create(blob: Blob): string {
    const safe = INERT_IMAGE_TYPES.has(baseType(blob.type))
      ? blob
      : new Blob([blob], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(safe);
    this.urls.add(url);
    return url;
  }

  /** How many URLs are currently outstanding; for tests and assertions. */
  get size(): number {
    return this.urls.size;
  }

  revokeAll(): void {
    for (const url of this.urls) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // A torn-down document can make this throw; there is nothing to do
        // about it and nothing left to leak once the document is gone.
      }
    }
    this.urls.clear();
  }
}

/** True for the proxy's "too many at once" refusal, which is worth re-asking. */
function isRateLimited(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

/**
 * True when the server refused this image on policy rather than failing to get
 * it: a tracking pixel, an SVG the reader has not allowed (or one that could
 * not be made safe), a host the guard denies, or remote content not enabled
 * for this message.
 *
 * The distinction is the whole point. A refusal is final -- the image will not
 * appear however many times it is asked for -- and telling the reader "the
 * request failed, nothing was blocked, sign in again" about an image the
 * server deliberately blocked is wrong in both directions at once: it denies
 * the block and it offers a remedy that cannot work.
 */
function isRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === ApiErrorCodes.RemoteContentNotAllowed
  );
}

/** True for the refusal the reader can change: an SVG they have not allowed. */
function isSvgRefusal(error: unknown): boolean {
  return isRefusal(error) && (error as ApiError).details?.reason === SVG_NOT_ALLOWED_REASON;
}

/** A `setTimeout` that gives up the moment the caller aborts. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * One subresource, re-asked while the proxy says "not now".
 *
 * The bounded pool above is what keeps this rare; this is what covers the case
 * the pool cannot see — a second window on the same account spending the same
 * server-side budget.
 */
async function fetchSubresource(
  src: string,
  fetchBlob: SubresourceFetcher,
  signal?: AbortSignal,
): Promise<Blob> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fetchBlob(src, signal);
    } catch (error) {
      if (attempt >= RATE_LIMITED_ATTEMPTS || !isRateLimited(error)) throw error;
      const asked = (error as ApiError).retryAfter;
      const wait =
        asked !== undefined && asked > 0 ? asked * 1000 : DEFAULT_RETRY_DELAY_MS * attempt;
      await sleep(Math.min(wait, MAX_RETRY_DELAY_MS), signal);
    }
  }
}

export interface SubresourceOutcome {
  /** The body HTML with every resolvable `src` rewritten to a `blob:` URL. */
  html: string;
  /** The stylesheet with every resolvable `url()` rewritten the same way. */
  css: string;
  /** How many `<img>` elements addressed our API and were therefore fetched. */
  requested: number;
  /** How many of those failed: asked for, not answered. Retrying may help. */
  failed: number;
  /** How many the server refused on policy. Retrying cannot help. */
  refused: number;
  /**
   * How many were SVGs the reader has not allowed. Not included in `refused`:
   * this is the one refusal the reader can change, and the pane offers it.
   */
  svgBlocked: number;
}

/** Cheap pre-check so an image-less body is never re-serialized at all. */
export function hasSubresources(html: string, css = ''): boolean {
  return /<img\b/i.test(html) || html.includes('url(') || css.includes('url(');
}

/**
 * The one `url()` shape the CSS pass writes (ADR 0081): unquoted, a path in a
 * narrow alphabet. Anything else in a url() is not the sanitizer's and is
 * never fetched.
 */
const CSS_URL = /url\(([^()\s'"\\]*)\)/g;

/** Every API URL a stylesheet or style attribute asks for. */
function cssURLs(css: string): string[] {
  const out: string[] = [];
  for (const m of css.matchAll(CSS_URL)) {
    const url = m[1] ?? '';
    if (isAuthenticatedSubresource(url)) out.push(url);
  }
  return out;
}

/**
 * Rewrites every url() to the `blob:` URL it was resolved to, and every other
 * url() -- one that failed, was refused, or was never ours -- to `none`,
 * which is valid wherever a background image is, so the colour beside it
 * still paints. `img-src blob:` would refuse the rest anyway; `none` keeps
 * the frame from asking.
 */
function rewriteCSSURLs(css: string, resolved: ReadonlyMap<string, string>): string {
  if (!css.includes('url(')) return css;
  return css.replace(CSS_URL, (_match, url: string) => {
    const blob = resolved.get(url);
    return blob ? `url(${blob})` : 'none';
  });
}

/**
 * Rewrites the sanitized body so the frame loads nothing for itself.
 *
 * Parsing happens in a `DOMParser` document, which has no browsing context and
 * therefore fetches nothing while we work on it — the markup is inspected, not
 * rendered. The input is the server sanitizer's output and nothing else, and
 * re-serializing through `innerHTML` re-escapes on the way out.
 *
 * This never rejects. A subresource that cannot be fetched is a fact about one
 * image, not a reason to fail the whole body, so the element keeps its box and
 * its alt text, gains `FAILED_SUBRESOURCE_CLASS`, and is counted.
 */
export async function resolveFrameSubresources(
  html: string,
  fetchBlob: SubresourceFetcher,
  registry: ObjectUrlRegistry,
  signal?: AbortSignal,
  options: ResolveOptions = {},
): Promise<SubresourceOutcome> {
  const css = options.css ?? '';
  const none = { html, css, requested: 0, failed: 0, refused: 0, svgBlocked: 0 };
  if (!hasSubresources(html, css)) return none;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img'));
  const styled = Array.from(doc.querySelectorAll('[style*="url("]'));
  if (images.length === 0 && styled.length === 0 && !css.includes('url(')) return none;
  const rasterize = options.rasterizeSvg ?? rasterizeSvg;

  // One entry per distinct URL: a message that embeds the same logo six times
  // should decode it once. A background image has no element of its own to
  // mark, so CSS references are counted per URL and resolved into `resolved`.
  const wanted = new Map<string, HTMLImageElement[]>();
  const cssWanted = new Map<string, number>();
  const resolved = new Map<string, string>();
  for (const url of [...styled.flatMap((el) => cssURLs(el.getAttribute('style') ?? '')), ...cssURLs(css)]) {
    cssWanted.set(url, (cssWanted.get(url) ?? 0) + 1);
    if (!wanted.has(url)) wanted.set(url, []);
  }

  for (const img of images) {
    // `srcset` is a second loader with its own URL list and the parent does
    // not resolve it. Left in place it would be a request the frame makes for
    // itself, which is the thing this whole module exists to prevent.
    img.removeAttribute('srcset');

    const src = img.getAttribute('src') ?? '';
    if (isAuthenticatedSubresource(src)) {
      const existing = wanted.get(src);
      if (existing) existing.push(img);
      else wanted.set(src, [img]);
      continue;
    }
    // The blocked-remote placeholder and anything else non-API: drop the src.
    // `img-src blob:` would refuse it anyway, and a refused request renders as
    // a broken-image glyph, which reads like a malfunction rather than like
    // the deliberate non-load it is. The CSS box stays.
    img.removeAttribute('src');
  }

  let failed = 0;
  let refused = 0;
  let svgBlocked = 0;
  let requested = 0;
  for (const [src, elements] of wanted) requested += elements.length + (cssWanted.get(src) ?? 0);

  // A bounded pool, not `Promise.all` over every entry: see
  // SUBRESOURCE_CONCURRENCY. Order does not matter — each entry writes only to
  // its own elements — so the workers just take the next index.
  const entries = Array.from(wanted);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = next;
      next += 1;
      const entry = entries[index];
      if (entry === undefined) return;
      const [src, elements] = entry;
      const uses = elements.length + (cssWanted.get(src) ?? 0);
      let blob: Blob;
      try {
        blob = await fetchSubresource(src, fetchBlob, signal);
      } catch (error) {
        const marker = isSvgRefusal(error)
          ? SVG_BLOCKED_SUBRESOURCE_CLASS
          : isRefusal(error)
            ? REFUSED_SUBRESOURCE_CLASS
            : FAILED_SUBRESOURCE_CLASS;
        if (marker === SVG_BLOCKED_SUBRESOURCE_CLASS) svgBlocked += uses;
        else if (marker === REFUSED_SUBRESOURCE_CLASS) refused += uses;
        else failed += uses;
        for (const element of elements) {
          element.removeAttribute('src');
          element.classList.add(marker);
        }
        continue;
      }

      if (isSvgBlob(blob)) {
        // Never minted as SVG: see the SVG section at the top of this file.
        // One that will not rasterize is refused, not passed through — a
        // retry would decode the same bytes the same way.
        let raster: RasterImage;
        try {
          raster = await rasterize(blob, signal);
        } catch {
          refused += uses;
          for (const element of elements) {
            element.removeAttribute('src');
            element.classList.add(REFUSED_SUBRESOURCE_CLASS);
          }
          continue;
        }
        const url = registry.create(raster.blob);
        resolved.set(src, url);
        for (const element of elements) {
          element.setAttribute('src', url);
          // The PNG is RASTER_SCALE times the SVG's size. An element the
          // sender did not size would otherwise render at the PNG's pixel
          // size; give it the size the SVG itself would have had. A sized
          // element keeps the sender's sizing (one attribute is enough: the
          // PNG has the SVG's aspect ratio).
          if (!element.hasAttribute('width') && !element.hasAttribute('height')) {
            element.setAttribute('width', String(raster.width));
            element.setAttribute('height', String(raster.height));
          }
        }
        continue;
      }

      const url = registry.create(blob);
      resolved.set(src, url);
      for (const element of elements) element.setAttribute('src', url);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(SUBRESOURCE_CONCURRENCY, entries.length) }, worker),
  );

  for (const el of styled) {
    el.setAttribute('style', rewriteCSSURLs(el.getAttribute('style') ?? '', resolved));
  }
  return { html: doc.body.innerHTML, css: rewriteCSSURLs(css, resolved), requested, failed, refused, svgBlocked };
}

/**
 * Hands an object URL to the browser as a download.
 *
 * `filename` must be `Attachment.filename` — the value the backend already
 * sanitized for its own `Content-Disposition` (doc 11 §2.4). It is not
 * re-derived from the URL or from any header here: `requestBlob` returns only
 * the body, and parsing a filename back out of user-controlled MIME on this
 * side would be re-introducing the exact injection the server pass removed.
 *
 * The URL is not revoked here. Revocation while the download is still being
 * handed over cancels it in some browsers, so the caller's registry owns the
 * lifetime and releases it when the message changes or the pane unmounts.
 */
export function triggerDownload(url: string, filename: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Turns a failed subresource fetch into words.
 *
 * Smaller than a failed message load: the message itself is on screen and
 * only one part is in trouble. The distinctions are the ones that matter for
 * the body too — a dead session is not an outage, and neither is offline.
 * Shared by the reading pane's inline images and by the attachment list.
 */
export function subresourceFailureCopy(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isSessionExpired) {
      return 'Your session needs confirming. Sign in again, then try this once more.';
    }
    if (cause.isOffline) return 'You’re offline. This will work once the connection is back.';
    if (cause.isServiceUnavailable) {
      return 'The mail store isn’t answering right now. Try again in a moment.';
    }
    return cause.message;
  }
  return cause instanceof Error && cause.message ? cause.message : 'The download failed.';
}
